import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { API } from "../lib/api";

/**
 * AudioPlayer — fetches authenticated audio blob and plays it.
 * If `publicMode` is true, fetches from the public route (no auth).
 */
export default function AudioPlayer({ sessionId, publicMode = false }) {
    const [src, setSrc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);

    useEffect(() => {
        let revoke;
        const load = async () => {
            try {
                setLoading(true);
                const url = publicMode ? `${API}/public/audio/${sessionId}` : `${API}/audio/${sessionId}`;
                const headers = publicMode
                    ? {}
                    : { Authorization: `Bearer ${localStorage.getItem("vf_token")}` };
                const r = await fetch(url, { headers });
                if (!r.ok) {
                    if (r.status === 404) throw new Error("Audio unavailable");
                    throw new Error("Could not load audio");
                }
                const blob = await r.blob();
                const blobUrl = URL.createObjectURL(blob);
                revoke = blobUrl;
                setSrc(blobUrl);
            } catch (e) {
                setError(e?.message || "Audio unavailable");
            } finally {
                setLoading(false);
            }
        };
        load();
        return () => { if (revoke) URL.revokeObjectURL(revoke); };
    }, [sessionId, publicMode]);

    const toggle = () => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) { a.play(); setIsPlaying(true); }
        else { a.pause(); setIsPlaying(false); }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-3 text-sm text-stone-500" data-testid="audio-loading">
                <span className="w-3 h-3 rounded-full bg-stone-300 animate-pulse" />
                Loading audio…
            </div>
        );
    }
    if (error || !src) {
        return <div className="text-sm text-stone-500 italic" data-testid="audio-unavailable">Audio not available for this session.</div>;
    }
    return (
        <div className="flex items-center gap-3" data-testid="audio-player">
            <button
                type="button"
                onClick={toggle}
                className="w-10 h-10 rounded-full bg-terracotta text-white flex items-center justify-center hover:scale-105 transition-transform"
                data-testid="audio-toggle"
                aria-label={isPlaying ? "Pause" : "Play"}
            >
                {isPlaying ? <Pause size={16} fill="white" /> : <Play size={16} fill="white" />}
            </button>
            <audio
                ref={audioRef}
                src={src}
                controls
                className="flex-1 min-w-0"
                onEnded={() => setIsPlaying(false)}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                data-testid="audio-element"
            />
        </div>
    );
}
